import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Paper, Button, TextField, CircularProgress,
    Avatar, Grid, Divider, Switch, alpha, useTheme, FormControlLabel,
    Alert, Collapse, InputAdornment, IconButton, MenuItem,
    ToggleButton, ToggleButtonGroup, Tab, Tabs,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    Autocomplete, Stack, Chip, Tooltip,
} from '@mui/material';
import {
    Business as BusinessIcon,
    WhatsApp as WhatsAppIcon,
    Save as SaveIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Link as LinkIcon,
    Api as ApiIcon,
    Info as InfoIcon,
    Message as MessageIcon,
    People as PeopleIcon,
    LocationOn as LocationOnIcon,
    Edit as EditIcon,
    Lock as LockIcon,
    Payments as PaymentsIcon,
    ContentCopy as ContentCopyIcon,
    Autorenew as AutorenewIcon,
    Download as DownloadIcon,
    HelpOutline as HelpOutlineIcon,
    SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import { useAppContext } from '../context/AppContext.js';
import ExpenseCategoryManager from './ExpenseCategoryManager.js';
import UserManagement from './UserManagement.js';
import SectorManager from './SectorManager.js';
import WhatsAppTemplatesManager from './WhatsAppTemplatesManager.js';
import WhatsAppConnectCard from './WhatsAppConnectCard.js';
import LicenseTab from './LicenseTab.js';
import AIConnectorCard from './AIConnectorCard';
import WhatsAppPublicUrlHelp from './WhatsAppPublicUrlHelp.js';
import { formatStamp } from './formatStamp';
import { describeStaleConnectors } from './connectorFiles';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

// Default WhatsApp settings (outside component to avoid stale closures)
const DEFAULT_WA = {
    enabled: false, mode: 'deeplink',
    phone_number_id: '', business_account_id: '', app_id: '',
    app_secret: '', access_token: '', api_version: 'v19.0',
    template_payment_paid: 'payment_confirmation',
    template_subscription_renewed: 'subscription_renewal',
    template_payment_reminder: 'payment_reminder',
    template_current_balance: 'current_balance',
    template_forward_alert: 'customer_reply_alert',
    template_bulk_outage: 'outage_alert',
    template_bulk_maintenance: 'maintenance_alert',
    template_bulk_feature: 'feature_update',
    template_bulk_offer: 'special_offer',
    template_forward_keepalive: 'daily_checkin',
    last_forwarding_keepalive_sent_at: null,
    template_language: 'en',
    forwarding_mobile: '',
    webhook_verify_token: 'delta_net_whatsapp_secret',
    auto_reply_enabled: true,
    auto_reply_message: "your message will be redirected to customer services team, they will respond in minutes, thank you.\n\nسيتم تحويل رسالتك الى قسم خدمة الزبائن, يقومون بالرد خلال دقائق, شكرا لكم",
    // eslint-disable-next-line no-template-curly-in-string
    deeplink_msg_payment: 'Dear {customer_name}, your payment of ${amount} has been received. Thank you!',
    // eslint-disable-next-line no-template-curly-in-string
    deeplink_msg_renewal: 'Dear {customer_name}, your subscription has been renewed until {expiry_date}. Thank you!',
    // eslint-disable-next-line no-template-curly-in-string
    deeplink_msg_payment_reminder: 'Dear {customer_name}, this is a friendly reminder that your subscription payment is due. Balance: ${balance}. Thank you!',
    // eslint-disable-next-line no-template-curly-in-string
    deeplink_msg_current_balance: 'Dear {customer_name}, your current balance is ${balance}. Expiry date: {expiry_date}. Thank you!',
};

// ── Section wrapper ──────────────────────────────────────────────────────────
const Section = ({ icon, title, subtitle, color, action, children }) => {
    const theme = useTheme();
    return (
        <Paper elevation={0} sx={{
            p: 3, mb: 3, borderRadius: '20px',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: `1px solid ${alpha(color || theme.palette.primary.main, 0.12)}`
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: '14px', bgcolor: alpha(color || theme.palette.primary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {React.cloneElement(icon, { sx: { color: color || theme.palette.primary.main, fontSize: 22 } })}
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{title}</Typography>
                        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
                    </Box>
                </Box>
                {action}
            </Box>
            {children}
        </Paper>
    );
};

// ── Main Component ────────────────────────────────────────────────────────────
// Escape a value for a TOML basic string ("..."). Device fields come from
// the tenant's own form input, so a quote or backslash must not break the file.
const tomlString = (value) => String(value ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    // eslint-disable-next-line no-control-regex -- stripping control chars is the point
    .replace(/[\u0000-\u001f\u007f]/g, '');

// agent.toml [[device]] blocks, pre-filled from the Network Devices page.
// Written live (not commented out) so the agent starts right away; until the
// owner types the passwords, those devices just read as auth failures in the
// app, which points straight at what's missing. With no devices registered,
// fall back to a commented example, since the agent needs at least one.
const buildAgentDeviceBlocks = (devices) => {
    const usable = (devices || []).filter((d) => d && d.id != null && d.host
        && (d.device_type === 'mikrotik_ccr' || d.device_type === 'vsol_olt'));
    if (usable.length === 0) {
        return `# No devices are registered yet. Add them on the Network Devices page,
# then add one block per device here and remove the # in front of its lines.
# The agent will NOT start until at least one device is filled in.
# id and host must match the Network Devices page exactly.

# [[device]]
# id       = 1
# host     = "192.168.8.1"
# type     = "mikrotik_ccr"
# api_port = 8728
# use_tls  = false
# username = "admin"
# password = "ROUTEROS-PASSWORD"

# [[device]]
# id       = 2
# host     = "192.168.8.100"
# type     = "vsol_olt"
# api_port = 161
# password = "SNMP-COMMUNITY"`;
    }
    const header = `# Pre-filled from your Network Devices page. Type each device's password
# between the quotes below. Do not change id or host -- they must match
# ServiceBills exactly. Passwords stay on this PC and are never uploaded.`;
    const blocks = usable.map((d) => {
        const isOlt = d.device_type === 'vsol_olt';
        const name = String(d.name || '').replace(/[\r\n]+/g, ' ');
        const lines = [
            `# ${name}`,
            '[[device]]',
            `id       = ${Number(d.id)}`,
            `host     = "${tomlString(d.host)}"`,
            `type     = "${d.device_type}"`,
            `api_port = ${Number(d.api_port) || (isOlt ? 161 : (d.use_tls ? 8729 : 8728))}`,
        ];
        if (isOlt) {
            lines.push('password = ""          # <-- type the SNMP community here');
        } else {
            lines.push(`use_tls  = ${d.use_tls ? 'true' : 'false'}`);
            lines.push(`username = "${tomlString(d.username || 'admin')}"`);
            lines.push('password = ""          # <-- type the RouterOS password here');
            if (d.service_name) lines.push(`service_name = "${tomlString(d.service_name)}"`);
        }
        return lines.join('\n');
    });
    return [header, ...blocks].join('\n\n');
};

// Hover help next to "On-prem Agent". The installer writes agent.toml with
// every device commented out (the cloud never holds device passwords in agent
// mode), so the owner has to fill it in by hand -- this is the only place in
// the app that tells them what goes there.
const AGENT_SETUP_HELP = (
    <Box sx={{ '& code': { fontFamily: 'monospace', bgcolor: 'rgba(255,255,255,0.15)', px: 0.5, borderRadius: '4px' } }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Setting up the agent</Typography>
        <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75 } }}>
            <li>Add your devices on the <strong>Network Devices</strong> page first. Then click <strong>Add local agent</strong> (or <strong>Regenerate Token</strong>). An installer script downloads with your token and devices built in.</li>
            <li>On an always-on PC on the same network as your devices, with Python 3.11+ installed, run it from the Downloads folder:<br />
                <code>powershell -ExecutionPolicy Bypass -File .\Install-ServiceBillsAgent.ps1</code></li>
            <li>Open Notepad <strong>as administrator</strong> and open <code>C:\ProgramData\ServiceBillsAgent\agent.toml</code>. Devices already on the <strong>Network Devices</strong> page are pre-filled, so just type each <code>password</code>. Any other device needs a block like this:
                <Box component="pre" sx={{ m: '6px 0 0', p: 1, borderRadius: '6px', bgcolor: 'rgba(0,0,0,0.3)', fontSize: '0.72rem', whiteSpace: 'pre', overflowX: 'auto' }}>
{`[[device]]
id       = 1
host     = "192.168.8.1"
type     = "mikrotik_ccr"   # or "vsol_olt"
api_port = 8728             # 161 for an OLT
username = "admin"
password = "device password"`}
                </Box>
            </li>
            <li><code>id</code> and <code>host</code> must match the device exactly as listed on the <strong>Network Devices</strong> page. The agent refuses any other host. For an OLT, <code>password</code> is the SNMP community and <code>username</code> is left out.</li>
            <li>Run the script again to start the agent. It keeps your <code>agent.toml</code>. The chip above turns <strong>Online</strong> within a few seconds.</li>
        </Box>
        <Typography variant="caption" component="div" sx={{ mt: 0.5, opacity: 0.85 }}>
            Device passwords stay on that PC only and are never sent to ServiceBills. To update the agent later, use <strong>Download Update Script</strong>.
        </Typography>
    </Box>
);

const SettingsView = ({ businessSettings, setBusinessSettings, setSnackbar, initialTab }) => {
    const { apiService, hasModule, modules, isOnprem } = useAppContext();
    const theme = useTheme();
    const [tab, setTab] = useState(initialTab || 'business');
    const SETTINGS_TABS = [
        { key: 'business', label: 'Business Details', icon: <BusinessIcon sx={{ fontSize: 18 }} /> },
        { key: 'wa-notifications', label: 'WhatsApp Notifications', icon: <WhatsAppIcon sx={{ fontSize: 18 }} />, module: 'whatsapp' },
        { key: 'wa-templates', label: 'WhatsApp Templates', icon: <WhatsAppIcon sx={{ fontSize: 18 }} />, module: 'whatsapp' },
        { key: 'whish', label: 'Whish Payments', icon: <PaymentsIcon sx={{ fontSize: 18 }} />, module: 'whish_payments' },
        { key: 'ai-connector', label: 'AI Connector', icon: <SmartToyIcon sx={{ fontSize: 18 }} />, module: 'ai_connector' },
        { key: 'expense-categories', label: 'Expense Categories', icon: <MessageIcon sx={{ fontSize: 18 }} /> },
        { key: 'users', label: 'User Management', icon: <PeopleIcon sx={{ fontSize: 18 }} /> },
        { key: 'sectors', label: 'Sectors', icon: <LocationOnIcon sx={{ fontSize: 18 }} /> },
        { key: 'license', label: 'License', icon: <LockIcon sx={{ fontSize: 18 }} />, onpremOnly: true },
    ].filter(t => !t.onpremOnly || isOnprem).filter(t => !t.module || hasModule(t.module));
    const activeTab = SETTINGS_TABS.some(t => t.key === tab) ? tab : 'business';

    // ── Business form state ───────────────────────────────────────────────────
    const [bizForm, setBizForm] = useState({
        business_name: '', address: '', mobile: '', email: '', website: '', network_mode: 'none',
        network_access_mode: 'direct',
        upstream_sync_automation_enabled: false,
        public_url: ''
    });
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [bizLoading, setBizLoading] = useState(false);

    useEffect(() => {
        if (businessSettings) {
            setBizForm({
                business_name: businessSettings.business_name || '',
                address: businessSettings.address || '',
                mobile: businessSettings.mobile || '',
                email: businessSettings.email || '',
                website: businessSettings.website || '',
                network_mode: businessSettings.network_mode || 'none',
                network_access_mode: businessSettings.network_access_mode || 'direct',
                upstream_sync_automation_enabled: !!businessSettings.upstream_sync_automation_enabled,
                public_url: businessSettings.public_url || ''
            });
            if (businessSettings.logo_url) {
                const url = businessSettings.logo_url;
                // Absolute already (S3/R2 presigned URL) or the frontend's own default asset — don't double-prefix.
                setLogoPreview(url.startsWith('http') || url === '/serviceBillsLogo.png' ? url : `${API_BASE_URL}${url}`);
            }
        }
    }, [businessSettings]);

    const handleBizSubmit = async (e) => {
        e.preventDefault();
        setBizLoading(true);
        const fd = new FormData();
        Object.keys(bizForm).forEach(k => { if (k !== 'public_url' || isOnprem) fd.append(k, bizForm[k]); });
        if (logoFile) fd.append('logo', logoFile);
        try {
            const response = await apiService.saveBusinessSettings(fd);
            setBusinessSettings(response.data.settings);
            setSnackbar({ open: true, message: 'Business settings saved!', severity: 'success' });
            if (isOnprem) fetchWASettings();
        } catch (err) {
            setSnackbar({ open: true, message: err?.response?.data?.msg || 'Failed to save settings.', severity: 'error' });
        } finally {
            setBizLoading(false);
        }
    };

    // ── Network agent management (agent-mode device access) ────────────────────
    // At most one agent per tenant (enforced server-side) -- `agents[0]` (or
    // null) is the whole picture. Fetched only while the form's current
    // access-mode selection is 'agent' -- see the effect below -- so nothing
    // hits this endpoint for a tenant that has never touched agent mode.
    const [agents, setAgents] = useState([]);
    const [agentsFetching, setAgentsFetching] = useState(false);
    const [agentActionLoading, setAgentActionLoading] = useState(false);
    const agent = agents[0] || null;

    const fetchAgents = useCallback(async () => {
        setAgentsFetching(true);
        try {
            const res = await apiService.fetchNetworkAgents();
            setAgents(res.data || []);
        } catch (e) {
            console.error('Failed to load network agents', e);
        } finally {
            setAgentsFetching(false);
        }
    }, [apiService]);

    useEffect(() => {
        if (bizForm.network_access_mode === 'agent' && hasModule('network')) fetchAgents();
    }, [bizForm.network_access_mode, fetchAgents, modules]); // eslint-disable-line react-hooks/exhaustive-deps

    // The token dialog is the ONLY place the plaintext token is ever held.
    // It lives in this one piece of state, cleared the moment the dialog
    // closes (see handleCloseTokenDialog) -- there is no way to recover it
    // afterwards, by design (the backend never stores or re-returns it).
    const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
    const [revealedToken, setRevealedToken] = useState(null);
    const [tokenCopied, setTokenCopied] = useState(false);
    const tokenInputRef = useRef(null);

    // Regenerating knocks the live agent offline immediately (it invalidates
    // the credential the running agent is authenticating with, per the Alert
    // in the agent panel below) with no undo -- same destructive shape as
    // the public pay link's Regenerate, so it gets the same confirm-dialog
    // gate rather than firing straight off the button.
    const [agentRegenerateConfirmOpen, setAgentRegenerateConfirmOpen] = useState(false);

    // token is optional: the token is only ever revealed once (right after
    // create/regenerate), so the "update" download from the agent card has
    // none. That's fine for an existing box -- the script preserves an
    // existing agent.toml and only refreshes the code files + task.
    const downloadInstallerScript = async (token) => {
        // Pre-fill agent.toml from the Network Devices page so id/host/type/
        // port can't be mistyped -- the agent refuses any host that doesn't
        // match exactly. Passwords are never in the cloud in agent mode, so
        // those stay blank for the owner to type. Only needed when this
        // script can create agent.toml at all, i.e. when it carries a token.
        let devices = [];
        if (token) {
            try {
                const res = await apiService.fetchNetworkDevices();
                devices = Array.isArray(res.data) ? res.data : [];
            } catch {
                devices = [];  // fall back to the commented example below
            }
        }
        const deviceBlocks = buildAgentDeviceBlocks(devices);
        const script = `# ServiceBills On-Premise Agent Automated Installer / Updater
# Safe to re-run: it stops the running agent, refreshes the code files,
# keeps your existing agent.toml, and starts the agent again.
param([switch]$Elevated)

# Self-elevate to Administrator if not already elevated
$isAdmin = ([Security.Principal.WindowsPrincipal][Security.Principal.WindowsIdentity]::GetCurrent()).IsInRole([Security.Principal.WindowsBuiltInRole]::Administrator)
if (-not $isAdmin) {
    if ($Elevated) {
        # Already relaunched once and still not admin -- stop, never loop.
        Write-Host "Could not get Administrator rights. Right-click PowerShell -> Run as administrator, then run this script again." -ForegroundColor Red
        Read-Host -Prompt "Press Enter to exit"
        Exit 1
    }
    Write-Host "Elevating permissions to Administrator..." -ForegroundColor Yellow
    Start-Process powershell.exe -Verb RunAs -ArgumentList ('-NoProfile -ExecutionPolicy Bypass -File "' + $PSCommandPath + '" -Elevated')
    Exit
}

$installDir = "C:\\ProgramData\\ServiceBillsAgent"
$binDir = "C:\\ServiceBills"
$repoUrl = "https://raw.githubusercontent.com/hasbach/ServiceBills/main"

Write-Host "==========================================================" -ForegroundColor Cyan
Write-Host "ServiceBills On-Premise Agent Auto-Installer" -ForegroundColor Cyan
Write-Host "==========================================================" -ForegroundColor Cyan

# Windows PowerShell 5.1 can default to TLS 1.0, which GitHub refuses.
[Net.ServicePointManager]::SecurityProtocol = [Net.ServicePointManager]::SecurityProtocol -bor [Net.SecurityProtocolType]::Tls12

Write-Host "1. Stopping any running agent so its files can be replaced..."
if (Get-ScheduledTask -TaskName "ServiceBillsAgent" -ErrorAction SilentlyContinue) {
    Stop-ScheduledTask -TaskName "ServiceBillsAgent" -ErrorAction SilentlyContinue
}
# Also catch an agent started by hand or by an older task -- Python keeps the
# old modules in memory, so new files do nothing until the process restarts.
Get-CimInstance Win32_Process -Filter "Name like 'python%'" -ErrorAction SilentlyContinue |
    Where-Object { $_.CommandLine -like '*servicebills_agent.py*' } |
    ForEach-Object { Stop-Process -Id $_.ProcessId -Force -ErrorAction SilentlyContinue }
Start-Sleep -Seconds 1

Write-Host "2. Creating directories..."
New-Item -ItemType Directory -Force -Path $installDir -ErrorAction SilentlyContinue | Out-Null
New-Item -ItemType Directory -Force -Path $binDir -ErrorAction SilentlyContinue | Out-Null
New-Item -ItemType Directory -Force -Path "$binDir\\agent" -ErrorAction SilentlyContinue | Out-Null

Write-Host "3. Downloading agent files..."
try {
    Invoke-WebRequest -UseBasicParsing -ErrorAction Stop -Uri "$repoUrl/agent/servicebills_agent.py" -OutFile "$binDir\\agent\\servicebills_agent.py"
    Invoke-WebRequest -UseBasicParsing -ErrorAction Stop -Uri "$repoUrl/agent/requirements-agent.txt" -OutFile "$binDir\\agent\\requirements-agent.txt"
    Invoke-WebRequest -UseBasicParsing -ErrorAction Stop -Uri "$repoUrl/mikrotik.py" -OutFile "$binDir\\mikrotik.py"
    Invoke-WebRequest -UseBasicParsing -ErrorAction Stop -Uri "$repoUrl/vsol_olt.py" -OutFile "$binDir\\vsol_olt.py"
} catch {
    Write-Host "Download failed: $_" -ForegroundColor Red
    Write-Host "Nothing was restarted. Check this PC's internet access and run the script again." -ForegroundColor Red
    Read-Host -Prompt "Press Enter to exit"
    Exit 1
}

$tomlPath = "$installDir\\agent.toml"
$hasToken = ${token ? '$true' : '$false'}
if (Test-Path $tomlPath) {
    Write-Host "4. Existing agent.toml found at $tomlPath (preserved)."
} elseif (-not $hasToken) {
    Write-Warning "4. No agent.toml exists on this PC and this script carries no token."
    Write-Warning "   In ServiceBills Settings, click Regenerate Token -- that downloads an installer with the token built in."
} else {
    Write-Host "4. Creating agent.toml configuration..."
    # Single-quoted here-string: no PowerShell expansion, so a device name
    # containing $ or a backtick lands in the file verbatim.
    $tomlContent = @'
cloud_url = "${tomlString(API_BASE_URL || window.location.origin)}"
token = "${tomlString(token || '')}"
poll_seconds = 2

${deviceBlocks}
'@
    # UTF-8 without BOM: Python's tomllib rejects a BOM, and PowerShell 5.1's
    # Set-Content would write ANSI and mangle non-English device names.
    [IO.File]::WriteAllText($tomlPath, $tomlContent)
    # Administrators need write access -- the next step is editing this file.
    icacls $tomlPath /inheritance:r /grant:r SYSTEM:R Administrators:F | Out-Null
}

Write-Host "5. Locating Python installation..."
$pythonPath = (Get-Command python.exe -ErrorAction SilentlyContinue).Source
# The WindowsApps python.exe is a Microsoft Store stub SYSTEM can't run.
if ($pythonPath -like '*\\WindowsApps\\*') { $pythonPath = $null }
if (-not $pythonPath) {
    $candidates = @(
        "C:\\Program Files\\Python313\\python.exe",
        "C:\\Program Files\\Python312\\python.exe",
        "C:\\Program Files\\Python311\\python.exe",
        "$env:LOCALAPPDATA\\Programs\\Python\\Python313\\python.exe",
        "$env:LOCALAPPDATA\\Programs\\Python\\Python312\\python.exe",
        "$env:LOCALAPPDATA\\Programs\\Python\\Python311\\python.exe"
    )
    foreach ($cand in $candidates) {
        if (Test-Path $cand) {
            $pythonPath = $cand
            break
        }
    }
}

if (-not $pythonPath) {
    Write-Warning "Python was not found on PATH or standard folders. Please install Python 3.11+."
} else {
    Write-Host "Found Python: $pythonPath" -ForegroundColor Green
    Write-Host "6. Installing agent dependencies..."
    # From requirements-agent.txt, not bare names: vsol_olt.py needs pysnmp 7.x.
    & $pythonPath -m pip install -r "$binDir\\agent\\requirements-agent.txt" --quiet

    Write-Host "7. Configuring Windows Scheduled Task (ServiceBillsAgent)..."
    try {
        $action = New-ScheduledTaskAction -Execute $pythonPath -Argument "C:\\ServiceBills\\agent\\servicebills_agent.py" -WorkingDirectory "C:\\ServiceBills"
        $trigger = New-ScheduledTaskTrigger -AtStartup
        $principal = New-ScheduledTaskPrincipal -UserId "SYSTEM" -LogonType ServiceAccount -RunLevel Highest
        
        $settingsParams = @{
            AllowStartIfOnBatteries = $true
            DontStopIfGoingOnBatteries = $true
            StartWhenAvailable = $true
            ExecutionTimeLimit = [TimeSpan]::Zero
            RestartInterval = (New-TimeSpan -Minutes 1)
            RestartCount = 999
        }
        $settings = New-ScheduledTaskSettingsSet @settingsParams

        Register-ScheduledTask -TaskName "ServiceBillsAgent" -Action $action -Trigger $trigger -Principal $principal -Settings $settings -Force | Out-Null
        
        Write-Host "8. Starting ServiceBillsAgent scheduled task..."
        Start-ScheduledTask -TaskName "ServiceBillsAgent"
        Start-Sleep -Seconds 2
        $task = Get-ScheduledTask -TaskName "ServiceBillsAgent" -ErrorAction SilentlyContinue
        Write-Host "Scheduled Task Status: $($task.State)" -ForegroundColor Green
    } catch {
        Write-Warning "Could not register scheduled task automatically: $_"
    }
}

Write-Host "==========================================================" -ForegroundColor Green
Write-Host "ServiceBills Agent installed and configured!" -ForegroundColor Green
Write-Host "Installation directory: $binDir"
Write-Host "Configuration file:    $tomlPath"
Write-Host "Log file:              $installDir\\agent.log"
Write-Host "==========================================================" -ForegroundColor Green
Write-Host "Please edit $tomlPath in Notepad to add your devices if you have not already."
Read-Host -Prompt "Press Enter to exit"
`;
        // Leading BOM so Windows PowerShell 5.1 reads the .ps1 as UTF-8, not
        // ANSI -- otherwise Arabic device names in it arrive garbled.
        const blob = new Blob(['﻿', script], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Install-ServiceBillsAgent.ps1';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleCreateAgent = async () => {
        setAgentActionLoading(true);
        try {
            const res = await apiService.createNetworkAgent({ name: 'Network Agent' });
            setAgents([res.data.agent]);
            const token = res.data.token;
            setRevealedToken(token);
            downloadInstallerScript(token);
            setTokenDialogOpen(true);
        } catch (err) {
            const detail = err?.response?.data?.error || 'Failed to create agent';
            setSnackbar({ open: true, message: detail, severity: 'error' });
        } finally {
            setAgentActionLoading(false);
        }
    };

    const handleRegenerateToken = async () => {
        if (!agent) return;
        setAgentActionLoading(true);
        try {
            const res = await apiService.regenerateNetworkAgentToken(agent.id);
            setAgents([res.data.agent]);
            const token = res.data.token;
            setRevealedToken(token);
            downloadInstallerScript(token);
            setTokenDialogOpen(true);
        } catch (err) {
            const detail = err?.response?.data?.error || 'Failed to regenerate token';
            setSnackbar({ open: true, message: detail, severity: 'error' });
        } finally {
            setAgentActionLoading(false);
            setAgentRegenerateConfirmOpen(false);
        }
    };

    const handleCopyToken = async () => {
        if (!revealedToken) return;
        // navigator.clipboard is undefined on plain HTTP (exactly how this
        // app is served in local dev, since the Clipboard API requires a
        // secure context) -- check for it up front rather than relying on
        // the throw, so that case degrades gracefully instead of surfacing
        // an error for something that isn't really a failure.
        if (navigator.clipboard && navigator.clipboard.writeText) {
            try {
                await navigator.clipboard.writeText(revealedToken);
                setTokenCopied(true);
                setTimeout(() => setTokenCopied(false), 2000);
                return;
            } catch (e) {
                // Fall through to the manual-selection fallback below.
            }
        }
        // Fallback: select the token text so the user can copy it manually
        // (Ctrl/Cmd+C), and make a best-effort attempt at execCommand('copy')
        // on top of that selection. Only surface an error if the selection
        // itself isn't possible -- a failed execCommand still leaves the
        // text selected and copyable by hand.
        try {
            const input = tokenInputRef.current;
            if (!input) throw new Error('Token field not available');
            input.focus();
            input.select();
            try { document.execCommand('copy'); } catch (_) { /* selection alone is still a usable fallback */ }
            setTokenCopied(true);
            setTimeout(() => setTokenCopied(false), 2000);
        } catch (e) {
            setSnackbar({ open: true, message: 'Could not copy token. Select the text and copy it manually.', severity: 'error' });
        }
    };

    // The only place the token is deliberately forgotten -- once this dialog
    // closes there is no way, anywhere in the app, to see this token again.
    const handleCloseTokenDialog = () => {
        setTokenDialogOpen(false);
        setRevealedToken(null);
        setTokenCopied(false);
    };

    // ── WhatsApp state ────────────────────────────────────────────────────────
    const [waForm, setWaForm] = useState(DEFAULT_WA);
    const [waLoading, setWaLoading] = useState(false);
    const [waFetching, setWaFetching] = useState(true);
    const [showSecret, setShowSecret] = useState(false);
    const [showToken, setShowToken] = useState(false);
    // Locked by default so a stray keystroke/paste can't corrupt a long
    // token/ID that's just sitting in the field being viewed -- must click
    // Edit to unlock, and it re-locks after every save.
    const [waCredsEditing, setWaCredsEditing] = useState(false);

    const [approvedTemplates, setApprovedTemplates] = useState([]);
    useEffect(() => {
        if (!hasModule('whatsapp')) return;
        apiService.fetchWhatsAppTemplates()
            .then(res => setApprovedTemplates((res.data.templates || []).filter(t => t.status === 'APPROVED')))
            .catch(() => {}); // Settings page still works with free-text fallback if this fails
    }, [apiService, modules]); // eslint-disable-line react-hooks/exhaustive-deps

    const [waWebhookUrl, setWaWebhookUrl] = useState('');
    const [publicUrlHelpOpen, setPublicUrlHelpOpen] = useState(false);
    // Automatic connection (WhatsAppConnectCard). The manual credential form
    // shows for manual tenants (incl. everyone from before this feature),
    // when the tenant picks "Manual configuration", or when the automatic
    // methods aren't offered at all (on-prem / no platform Meta app).
    const [waConnection, setWaConnection] = useState(null);
    const [waManualSelected, setWaManualSelected] = useState(false);
    const [waAutoAvailable, setWaAutoAvailable] = useState(null);
    const waShowManual = !waConnection || waConnection.method === 'manual' || waManualSelected || waAutoAvailable === false;
    // `silent` refetches without the full-tab spinner, so the connect card
    // (and an in-progress Meta sign-in) stays mounted.
    const fetchWASettings = useCallback(async ({ silent = false } = {}) => {
        if (!hasModule('whatsapp')) { setWaFetching(false); return; }
        if (!silent) setWaFetching(true);
        try {
            const res = await apiService.fetchWhatsAppSettings();
            if (res.data?.settings) setWaForm(prev => ({ ...DEFAULT_WA, ...res.data.settings }));
            setWaWebhookUrl(res.data?.webhook_url || '');
            setWaConnection(res.data?.connection || null);
        } catch (e) {
            console.error('Failed to load WhatsApp settings', e);
        } finally {
            if (!silent) setWaFetching(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiService, modules]);

    useEffect(() => { fetchWASettings(); }, [fetchWASettings]);

    const handleWASave = async () => {
        setWaLoading(true);
        try {
            await apiService.saveWhatsAppSettings(waForm);
            setSnackbar({ open: true, message: 'WhatsApp settings saved!', severity: 'success' });
            setWaCredsEditing(false);
        } catch (err) {
            const detail = err?.response?.data?.error || err?.response?.data?.msg || err?.message || 'Unknown error';
            console.error('WhatsApp save error:', err?.response || err);
            setSnackbar({ open: true, message: `Failed to save: ${detail}`, severity: 'error' });
        } finally {
            setWaLoading(false);
        }
    };

    const [linkingWaba, setLinkingWaba] = useState(false);
    const handleLinkWaba = async () => {
        setLinkingWaba(true);
        try {
            await apiService.saveWhatsAppSettings(waForm);
            const res = await apiService.subscribeWaba();
            setSnackbar({ open: true, message: res?.data?.message || 'Successfully linked Webhook to Meta Account!', severity: 'success' });
        } catch (err) {
            const detail = err?.response?.data?.error || err?.response?.data?.msg || err?.message || 'Unknown error';
            setSnackbar({ open: true, message: `Failed to link: ${detail}`, severity: 'error' });
        } finally {
            setLinkingWaba(false);
        }
    };

    const waField = (key) => ({ value: waForm[key], onChange: (e) => setWaForm(f => ({ ...f, [key]: e.target.value })) });

    // ── Whish Payments is a feature module (was the Pro plan flag) ──────────────
    const isPro = hasModule('whish_payments');

    // ── Tenant Whish (customer payments) settings state ─────────────────────────
    const DEFAULT_TWS = { enabled: false, whish_channel: '', whish_secret: '', display_name_override: '', configured: false };
    const [twsForm, setTwsForm] = useState(DEFAULT_TWS);
    const [twsLoading, setTwsLoading] = useState(false);
    const [twsFetching, setTwsFetching] = useState(true);
    const [showTwsSecret, setShowTwsSecret] = useState(false);
    // Locked by default, same rationale as waCredsEditing above -- must click
    // Edit to unlock, re-locks after every save.
    const [twsCredsEditing, setTwsCredsEditing] = useState(false);

    const fetchTwsSettings = useCallback(async () => {
        if (!hasModule('whish_payments')) { setTwsFetching(false); return; }
        setTwsFetching(true);
        try {
            const res = await apiService.tenantWhishSettings();
            if (res.data?.settings) setTwsForm(prev => ({ ...DEFAULT_TWS, ...res.data.settings }));
        } catch (e) {
            console.error('Failed to load Whish payment settings', e);
        } finally {
            setTwsFetching(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiService, modules]);

    useEffect(() => { fetchTwsSettings(); }, [fetchTwsSettings]);

    const handleTwsSave = async () => {
        setTwsLoading(true);
        try {
            const res = await apiService.saveTenantWhishSettings(twsForm);
            if (res.data?.settings) setTwsForm(prev => ({ ...DEFAULT_TWS, ...res.data.settings }));
            setSnackbar({ open: true, message: 'Whish payment settings saved!', severity: 'success' });
            setTwsCredsEditing(false);
        } catch (err) {
            const detail = err?.response?.data?.msg || err?.response?.data?.error || err?.message || 'Unknown error';
            console.error('Whish settings save error:', err?.response || err);
            setSnackbar({ open: true, message: `Failed to save: ${detail}`, severity: 'error' });
        } finally {
            setTwsLoading(false);
        }
    };

    const twsField = (key) => ({ value: twsForm[key], onChange: (e) => setTwsForm(f => ({ ...f, [key]: e.target.value })) });

    // ── Tenant-wide public payment page link (Task 20) ──────────────────────────
    const [publicPayLink, setPublicPayLink] = useState(null); // { slug, url } | null while loading
    const [publicPayLinkLoading, setPublicPayLinkLoading] = useState(false);
    const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    const fetchPublicPayLink = useCallback(async () => {
        try {
            const res = await apiService.getPublicPayLink();
            if (res.data?.slug) {
                setPublicPayLink(res.data);
            } else {
                // Lazily generate on first load -- staff shouldn't need an
                // extra manual step just to get a working link the first
                // time they open this tab. Never auto-regenerates an
                // EXISTING slug (that's the explicit, confirmed action
                // below) -- only fires when none has been generated yet.
                const gen = await apiService.regeneratePublicPayLink();
                setPublicPayLink(gen.data);
            }
        } catch (e) {
            console.error('Failed to load public payment page link', e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiService]);

    useEffect(() => { if (isPro) fetchPublicPayLink(); }, [isPro, fetchPublicPayLink]);

    const generateOrRegeneratePublicPayLink = async () => {
        setPublicPayLinkLoading(true);
        try {
            const res = await apiService.regeneratePublicPayLink();
            setPublicPayLink(res.data);
            setSnackbar({ open: true, message: 'Public payment page link generated!', severity: 'success' });
        } catch (err) {
            const detail = err?.response?.data?.msg || err?.message || 'Unknown error';
            setSnackbar({ open: true, message: `Failed to generate link: ${detail}`, severity: 'error' });
        } finally {
            setPublicPayLinkLoading(false);
            setRegenerateConfirmOpen(false);
        }
    };

    const copyPublicPayLink = async () => {
        if (!publicPayLink?.url) return;
        try {
            await navigator.clipboard.writeText(publicPayLink.url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            setSnackbar({ open: true, message: 'Could not copy link.', severity: 'error' });
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, background: 'linear-gradient(135deg, #f6f9fc 0%, #ffffff 100%)', minHeight: '100vh' }}>

            {/* Header */}
            <Paper elevation={0} sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4, borderRadius: '24px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: alpha('#fff', 0.1) }} />
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.3rem', sm: '1.75rem', md: '2.125rem' } }}>Settings</Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9, fontSize: { xs: '0.85rem', sm: '1rem' } }}>Configure your business profile, messaging and integrations</Typography>
                </Box>
            </Paper>

            {/* Tabs */}
            <Paper elevation={0} sx={{ borderRadius: '16px', mb: 3, overflow: 'hidden', border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Tabs value={activeTab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ px: { xs: 0, sm: 2 }, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
                    {SETTINGS_TABS.map(t => <Tab key={t.key} value={t.key} icon={t.icon} iconPosition="start" label={t.label} />)}
                </Tabs>
            </Paper>

            {/* ── Tab 0: Business Details ── */}
            {activeTab === 'business' && (
                <Section icon={<BusinessIcon />} title="Business Details" subtitle="Your company information shown on receipts" color={theme.palette.primary.main}>
                    <form onSubmit={handleBizSubmit}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Avatar src={logoPreview} sx={{ width: 100, height: 100, mb: 2, boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.2)}` }} />
                                <Button variant="outlined" component="label" sx={{ borderRadius: '10px', textTransform: 'none' }}>
                                    Upload Logo
                                    <input type="file" hidden accept="image/*" onChange={e => { const f = e.target.files[0]; if (f) { setLogoFile(f); setLogoPreview(URL.createObjectURL(f)); } }} />
                                </Button>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label="Business Name" value={bizForm.business_name} onChange={e => setBizForm(f => ({ ...f, business_name: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label="Mobile" value={bizForm.mobile} onChange={e => setBizForm(f => ({ ...f, mobile: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Address" value={bizForm.address} onChange={e => setBizForm(f => ({ ...f, address: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label="Email" type="email" value={bizForm.email} onChange={e => setBizForm(f => ({ ...f, email: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label="Website" value={bizForm.website} onChange={e => setBizForm(f => ({ ...f, website: e.target.value }))} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth select label="Network Integration" value={bizForm.network_mode}
                                    onChange={e => setBizForm(f => ({ ...f, network_mode: e.target.value }))}
                                    helperText="How your network actually works — controls whether Upstream Providers appears in the menu, and which network fields show on the Subscriptions form."
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                                    <MenuItem value="none">None — manage subscriptions/payments only</MenuItem>
                                    <MenuItem value="upstream_bridge">Bridged — I'm a subreseller on an upstream's RADIUS portal</MenuItem>
                                    <MenuItem value="local_mikrotik">Self-hosted — I run my own Mikrotik with local PPPoE</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <Collapse in={bizForm.network_mode === 'upstream_bridge'}>
                                    <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: '12px', mb: 1 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={bizForm.upstream_sync_automation_enabled}
                                                    onChange={e => setBizForm(f => ({ ...f, upstream_sync_automation_enabled: e.target.checked }))}
                                                />
                                            }
                                            label="Automatically refresh upstream status daily (beta)"
                                        />
                                        <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.85 }}>
                                            When on, each linked customer's upstream status/expiry is refreshed automatically once a day
                                            instead of only when someone clicks "Refresh" on their record. Read-only — this never
                                            suspends or unsuspends anyone's connection on its own, it only keeps the status shown in
                                            ServiceBills up to date. Off by default while this rolls out.
                                        </Typography>
                                    </Alert>
                                </Collapse>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth select label="Network Device Access" value={bizForm.network_access_mode}
                                    onChange={e => setBizForm(f => ({ ...f, network_access_mode: e.target.value }))}
                                    helperText="How ServiceBills reaches your own network hardware (Network Devices / Network Tree pages) for health checks."
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}>
                                    <MenuItem value="direct">Direct — this app can reach your devices itself</MenuItem>
                                    <MenuItem value="agent">Via on-prem agent — devices sit on a private LAN this app can't reach</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <Collapse in={bizForm.network_access_mode === 'agent'}>
                                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '12px' }}>
                                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>On-prem Agent</Typography>
                                            {/* enterTouchDelay=0 so a tap opens it on phones, where there's no hover. */}
                                            <Tooltip arrow placement="bottom-start" enterTouchDelay={0} leaveTouchDelay={15000}
                                                componentsProps={{ tooltip: { sx: { maxWidth: 440, p: 1.5, fontSize: '0.8rem' } } }}
                                                title={AGENT_SETUP_HELP}>
                                                <IconButton size="small" aria-label="How to set up the on-prem agent">
                                                    <HelpOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                        {agentsFetching ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
                                        ) : agent ? (
                                            <Box>
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
                                                    <Chip size="small" color={agent.is_online ? 'success' : 'error'}
                                                        label={agent.is_online ? 'Online' : 'Offline'} />
                                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{agent.name}</Typography>
                                                </Stack>
                                                <Typography variant="body2" color="text.secondary">
                                                    Last seen: {agent.last_seen_at ? formatStamp(agent.last_seen_at) : 'never connected'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Version: {agent.agent_version || 'unknown'}
                                                </Typography>
                                                {/* The version above comes from servicebills_agent.py alone, so it
                                                    can read as freshly bumped while a connector beside it is three
                                                    deploys old -- and a stale connector fails by quietly returning
                                                    nothing, not by erroring. This line is what makes that visible. */}
                                                {agent.connectors_status === 'stale' ? (
                                                    <Alert severity="warning" sx={{ mt: 1.5, mb: 2, borderRadius: '12px' }}>
                                                        <strong>Out of date on the agent box: {describeStaleConnectors(agent.stale_connectors)}.</strong>{' '}
                                                        The version above only covers <code>servicebills_agent.py</code>, so the agent
                                                        can look current while running an old connector — checks may return nothing
                                                        instead of failing. Click <strong>Download Update Script</strong> below and run it
                                                        on the agent box — it replaces the files and restarts the agent, keeping your agent.toml.
                                                    </Alert>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                        Connector files: {agent.connectors_status === 'current'
                                                            ? 'match this server'
                                                            : 'not reported — restart the agent to check'}
                                                    </Typography>
                                                )}
                                                <Button variant="outlined" color="warning" onClick={() => setAgentRegenerateConfirmOpen(true)} disabled={agentActionLoading}
                                                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
                                                    {agentActionLoading ? 'Regenerating…' : 'Regenerate Token'}
                                                </Button>
                                                {/* No token here -- it's only revealed once. The script keeps the
                                                    box's existing agent.toml, so it works as an in-place updater. */}
                                                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => downloadInstallerScript(null)}
                                                    sx={{ ml: 1.5, borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
                                                    Download Update Script
                                                </Button>
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                                    To run it on the agent box, open PowerShell in the folder it downloaded to and run:
                                                </Typography>
                                                <Box component="code" sx={{ display: 'block', mt: 0.5, p: 1, borderRadius: '8px', bgcolor: 'action.hover', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                                                    powershell -ExecutionPolicy Bypass -File .\Install-ServiceBillsAgent.ps1
                                                </Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                    Your existing <code>agent.toml</code> (token and devices) is left untouched.
                                                </Typography>
                                                <Alert severity="info" sx={{ mt: 2, borderRadius: '12px' }}>
                                                    Regenerating invalidates the current token immediately — the running agent will
                                                    stop authenticating until <code>agent.toml</code> is updated with the new one.
                                                </Alert>
                                            </Box>
                                        ) : (
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                    No agent has been created for this tenant yet. Create one to get a token to
                                                    paste into <code>agent.toml</code> on the on-prem box that can reach your devices.
                                                </Typography>
                                                <Button variant="contained" onClick={handleCreateAgent} disabled={agentActionLoading}
                                                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
                                                    {agentActionLoading ? 'Creating…' : 'Add local agent'}
                                                </Button>
                                            </Box>
                                        )}
                                    </Paper>
                                </Collapse>
                            </Grid>
                        </Grid>
                        <Button type="submit" variant="contained" startIcon={bizLoading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />} disabled={bizLoading}
                            sx={{ mt: 3, borderRadius: '12px', textTransform: 'none', fontWeight: 600, px: 4, py: 1.5 }}>
                            {bizLoading ? 'Saving…' : 'Save Business Details'}
                        </Button>
                    </form>
                </Section>
            )}

            {/* ── Tab 1: WhatsApp ── */}
            {activeTab === 'wa-notifications' && (
                <Box>
                    {waFetching ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
                    ) : (
                        <>
                            {/* Automatic connection: method choice + status */}
                            <WhatsAppConnectCard
                                connection={waConnection}
                                manualVisible={waShowManual && waForm.mode === 'api'}
                                onAutomaticAvailable={setWaAutoAvailable}
                                onSelectManual={(on) => {
                                    setWaManualSelected(on);
                                    if (on) setWaForm(f => ({ ...f, mode: 'api' }));
                                }}
                                onConnectionChange={(conn) => {
                                    setWaConnection(conn);
                                    fetchWASettings({ silent: true });
                                }}
                            />

                            {/* Master Toggle */}
                            <Section icon={<WhatsAppIcon />} title="WhatsApp Notifications" subtitle="Send messages to customers on payment or renewal" color="#25D366">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                    <Box>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>Enable WhatsApp Notifications</Typography>
                                        <Typography variant="body2" color="text.secondary">Show WhatsApp action buttons on payment records</Typography>
                                    </Box>
                                    <Switch checked={waForm.enabled} onChange={e => setWaForm(f => ({ ...f, enabled: e.target.checked }))}
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#25D366' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#25D366' } }} />
                                </Box>

                                <Divider sx={{ my: 3 }} />
                                {/* Mode Toggle */}
                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Sending Mode</Typography>
                                    <ToggleButtonGroup value={waForm.mode} exclusive onChange={(_, v) => v && setWaForm(f => ({ ...f, mode: v }))} sx={{ gap: 1 }}>
                                        <ToggleButton value="deeplink" sx={{ borderRadius: '12px !important', textTransform: 'none', px: 2, py: 1, border: '1px solid !important', fontWeight: 600, '&.Mui-selected': { bgcolor: alpha('#25D366', 0.1), borderColor: '#25D366 !important', color: '#25D366' } }}>
                                            <LinkIcon sx={{ mr: 1, fontSize: 18 }} /> Deep Link (Manual)
                                        </ToggleButton>
                                        <ToggleButton value="api" sx={{ borderRadius: '12px !important', textTransform: 'none', px: 2, py: 1, border: '1px solid !important', fontWeight: 600, '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.1), borderColor: `${theme.palette.primary.main} !important`, color: theme.palette.primary.main } }}>
                                            <ApiIcon sx={{ mr: 1, fontSize: 18 }} /> Meta Cloud API (Auto)
                                        </ToggleButton>
                                    </ToggleButtonGroup>

                                    <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2, borderRadius: '12px' }}>
                                        {waForm.mode === 'deeplink'
                                            ? '📱 A "Send via WhatsApp" button will appear on payment cards. Clicking it opens WhatsApp with a pre-filled message — the user taps Send.'
                                            : '🤖 Messages are sent automatically via the Meta Cloud API when a payment is marked as paid or a subscription is renewed. Requires approved message templates.'}
                                    </Alert>
                                </Box>
                            </Section>

                            {/* Deep Link Message Templates */}
                            <Collapse in={waForm.mode === 'deeplink'}>
                                <Section icon={<MessageIcon />} title="Message Templates — Deep Link" subtitle="Use {customer_name}, {amount}, {balance}, {expiry_date} as placeholders" color="#25D366">
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField fullWidth multiline rows={3} label="Payment Received Message"
                                                {...waField('deeplink_msg_payment')}
                                                helperText="Placeholders: {customer_name}, {amount}"
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField fullWidth multiline rows={3} label="Subscription Renewed Message"
                                                {...waField('deeplink_msg_renewal')}
                                                helperText="Placeholders: {customer_name}, {expiry_date}"
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField fullWidth multiline rows={3} label="Payment Reminder Message"
                                                {...waField('deeplink_msg_payment_reminder')}
                                                helperText="Sent from the Subscriptions page reminder dialog. Placeholders: {customer_name}, {balance}, {expiry_date}"
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField fullWidth multiline rows={3} label="Current Balance Message"
                                                {...waField('deeplink_msg_current_balance')}
                                                helperText="Sent from the Subscriptions page reminder dialog. Placeholders: {customer_name}, {balance}, {expiry_date}"
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                        </Grid>
                                    </Grid>
                                    <Alert severity="success" sx={{ mt: 2, borderRadius: '12px' }}>
                                        <strong>Preview:</strong> {waForm.deeplink_msg_payment.replace('{customer_name}', 'John Doe').replace('{amount}', '50.00')}
                                    </Alert>
                                </Section>
                            </Collapse>

                            {/* Meta API Settings: manual configuration only (own Meta app) */}
                            <Collapse in={waForm.mode === 'api' && waShowManual}>
                                <Section icon={<ApiIcon />} title="Meta Cloud API Credentials"
                                    subtitle="Get these from your Meta for Developers dashboard (developers.facebook.com)" color={theme.palette.primary.main}
                                    action={
                                        <Button
                                            size="small"
                                            variant={waCredsEditing ? 'contained' : 'outlined'}
                                            color={waCredsEditing ? 'warning' : 'primary'}
                                            startIcon={waCredsEditing ? <LockIcon /> : <EditIcon />}
                                            onClick={() => setWaCredsEditing(e => !e)}
                                            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                                        >
                                            {waCredsEditing ? 'Lock' : 'Edit'}
                                        </Button>
                                    }>
                                    <Alert severity="warning" sx={{ mb: 3, borderRadius: '12px' }}>
                                        ⚠️ This section stores your API credentials. Keep these secret — never share them. You must have a verified Meta Business account and approved message templates.
                                        {!waCredsEditing && ' Fields are locked — click Edit above to change them.'}
                                    </Alert>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <TextField fullWidth label="App ID" placeholder="e.g. 123456789012345" {...waField('app_id')}
                                                disabled={!waCredsEditing}
                                                helperText="From your Meta App Dashboard → App ID"
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField fullWidth label="App Secret" type={showSecret ? 'text' : 'password'} placeholder="Your app secret"
                                                {...waField('app_secret')} disabled={!waCredsEditing} helperText="Settings → Basic → App Secret"
                                                InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowSecret(s => !s)}>{showSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}</IconButton></InputAdornment> }}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField fullWidth label="Phone Number ID" placeholder="e.g. 103843228738291" {...waField('phone_number_id')}
                                                disabled={!waCredsEditing}
                                                helperText="WhatsApp → API Setup → Phone Number ID"
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField fullWidth label="Business Account ID (WABA ID)" placeholder="e.g. 102290129000001" {...waField('business_account_id')}
                                                disabled={!waCredsEditing}
                                                helperText="WhatsApp → API Setup → WhatsApp Business Account ID"
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField fullWidth label="Permanent Access Token" type={showToken ? 'text' : 'password'} placeholder="EAA..." multiline={showToken} rows={showToken ? 3 : 1}
                                                {...waField('access_token')} disabled={!waCredsEditing} helperText="System User token from Business Settings → System Users → Generate Token"
                                                InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowToken(s => !s)}>{showToken ? <VisibilityOffIcon /> : <VisibilityIcon />}</IconButton></InputAdornment> }}
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField fullWidth label="API Version" placeholder="v19.0" {...waField('api_version')}
                                                helperText="Latest stable: v19.0"
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{ my: 3 }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: theme.palette.primary.main }}>
                                        🔗 Webhook (your own Meta app)
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <TextField fullWidth label="Webhook Verify Token" placeholder="delta_net_whatsapp_secret" {...waField('webhook_verify_token')}
                                                helperText="Use this exact secret token in Meta Developer Console when configuring your Webhook"
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Alert severity="success" sx={{ borderRadius: '12px', bgcolor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', mb: 2 }}>
                                                <strong>Your Webhook URL for Meta Console:</strong> <code>{waWebhookUrl || `${window.location.origin}/api/whatsapp/webhook`}</code>
                                            </Alert>
                                            {isOnprem && (
                                                <Box sx={{ mb: 2 }}>
                                                    <TextField fullWidth size="small" label="Public URL" placeholder="https://billing.example.com"
                                                        value={bizForm.public_url}
                                                        onChange={e => setBizForm(f => ({ ...f, public_url: e.target.value }))}
                                                        helperText="The internet address of this PC. Used for the WhatsApp webhook and customer payment links." />
                                                    <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                        <Button size="small" variant="outlined" disabled={bizLoading} onClick={handleBizSubmit}
                                                            sx={{ textTransform: 'none' }}>
                                                            Save Public URL
                                                        </Button>
                                                        <Button size="small" sx={{ textTransform: 'none' }} onClick={() => setPublicUrlHelpOpen(true)}>
                                                            How do I make this PC reachable from the internet?
                                                        </Button>
                                                    </Box>
                                                    <WhatsAppPublicUrlHelp open={publicUrlHelpOpen} onClose={() => setPublicUrlHelpOpen(false)} />
                                                </Box>
                                            )}
                                            <Button variant="outlined" color="primary" onClick={handleLinkWaba} disabled={linkingWaba}
                                                sx={{ borderRadius: '12px', fontWeight: 600, textTransform: 'none' }}>
                                                {linkingWaba ? 'Linking to Meta Account...' : '🔗 Force Link Webhook to Meta Account'}
                                            </Button>
                                        </Grid>
                                    </Grid>

                                    <Box sx={{ mt: 3, p: 2, borderRadius: '12px', bgcolor: alpha(theme.palette.primary.main, 0.05), border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}` }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>📚 Quick Setup Reference</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            1. Go to <strong>developers.facebook.com</strong> → Create App → Business type<br />
                                            2. Add <strong>WhatsApp</strong> product → Get Phone Number ID + WABA ID<br />
                                            3. Create a <strong>System User</strong> in Business Settings → Generate a token with both <strong>whatsapp_business_messaging</strong> and <strong>whatsapp_business_management</strong> permissions (the second is required to create/edit/delete templates from this app)<br />
                                            4. Create and manage your message templates directly in the <strong>WhatsApp Templates</strong> tab above — no need to go to Meta Business Manager for template creation or approval status anymore, only to obtain your account/App ID/credentials<br />
                                            5. Paste credentials above and save<br />
                                            6. For real-time approval-status updates, enable <strong>message_template_status_update</strong> as a subscribed webhook field in your Meta App Dashboard (App configuration on Meta's side — this app's UI can't set it for you)
                                        </Typography>
                                    </Box>
                                </Section>
                            </Collapse>

                            {/* Meta API Settings shared by every connection method */}
                            <Collapse in={waForm.mode === 'api'}>
                                <Section icon={<MessageIcon />} title="Automatic Messages (Meta Cloud API)"
                                    subtitle="Template names, reply forwarding and auto-reply" color={theme.palette.primary.main}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                            <TextField fullWidth label="Template Language Code" placeholder="en" {...waField('template_language')}
                                                helperText="e.g. en, ar, fr, es"
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{ my: 3 }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Approved Template Names</Typography>
                                    <Alert severity="info" sx={{ mb: 2, borderRadius: '12px' }}>
                                        Pick from your templates approved via the <strong>WhatsApp Templates</strong> tab above (use its Refresh button to pull in anything approved directly in Meta), or type a template name directly if you haven't synced yet.
                                    </Alert>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={3}>
                                            <Autocomplete
                                                freeSolo
                                                fullWidth
                                                options={approvedTemplates.map(t => t.name)}
                                                value={waForm.template_payment_paid}
                                                onChange={(_, newValue) => setWaForm(f => ({ ...f, template_payment_paid: newValue || '' }))}
                                                onInputChange={(_, newInputValue) => setWaForm(f => ({ ...f, template_payment_paid: newInputValue }))}
                                                renderInput={(params) => (
                                                    <TextField {...params} fullWidth label="Payment Received Template"
                                                        helperText="Triggered when a payment is marked as paid"
                                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Autocomplete
                                                freeSolo
                                                fullWidth
                                                options={approvedTemplates.map(t => t.name)}
                                                value={waForm.template_subscription_renewed}
                                                onChange={(_, newValue) => setWaForm(f => ({ ...f, template_subscription_renewed: newValue || '' }))}
                                                onInputChange={(_, newInputValue) => setWaForm(f => ({ ...f, template_subscription_renewed: newInputValue }))}
                                                renderInput={(params) => (
                                                    <TextField {...params} fullWidth label="Subscription Renewed Template"
                                                        helperText="Triggered when subscription is renewed"
                                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Autocomplete
                                                freeSolo
                                                fullWidth
                                                options={approvedTemplates.map(t => t.name)}
                                                value={waForm.template_payment_reminder}
                                                onChange={(_, newValue) => setWaForm(f => ({ ...f, template_payment_reminder: newValue || '' }))}
                                                onInputChange={(_, newInputValue) => setWaForm(f => ({ ...f, template_payment_reminder: newInputValue }))}
                                                renderInput={(params) => (
                                                    <TextField {...params} fullWidth label="Payment Reminder Template"
                                                        helperText="For future manual or scheduled reminders"
                                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Autocomplete
                                                freeSolo
                                                fullWidth
                                                options={approvedTemplates.map(t => t.name)}
                                                value={waForm.template_current_balance}
                                                onChange={(_, newValue) => setWaForm(f => ({ ...f, template_current_balance: newValue || '' }))}
                                                onInputChange={(_, newInputValue) => setWaForm(f => ({ ...f, template_current_balance: newInputValue }))}
                                                renderInput={(params) => (
                                                    <TextField {...params} fullWidth label="Current Balance Template"
                                                        helperText="For balance & expiry reminders"
                                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Autocomplete
                                                freeSolo
                                                fullWidth
                                                options={approvedTemplates.map(t => t.name)}
                                                value={waForm.template_forward_alert}
                                                onChange={(_, newValue) => setWaForm(f => ({ ...f, template_forward_alert: newValue || '' }))}
                                                onInputChange={(_, newInputValue) => setWaForm(f => ({ ...f, template_forward_alert: newInputValue }))}
                                                renderInput={(params) => (
                                                    <TextField {...params} fullWidth label="Forwarding Alert Template (unused)"
                                                        helperText="Not sent by the current forwarding flow (see Daily Keep-Alive Template below) — kept only in case you revert to template-only alerts"
                                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Autocomplete
                                                freeSolo
                                                fullWidth
                                                options={approvedTemplates.map(t => t.name)}
                                                value={waForm.template_bulk_outage}
                                                onChange={(_, newValue) => setWaForm(f => ({ ...f, template_bulk_outage: newValue || '' }))}
                                                onInputChange={(_, newInputValue) => setWaForm(f => ({ ...f, template_bulk_outage: newInputValue }))}
                                                renderInput={(params) => (
                                                    <TextField {...params} fullWidth label="Outage Template"
                                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Autocomplete
                                                freeSolo
                                                fullWidth
                                                options={approvedTemplates.map(t => t.name)}
                                                value={waForm.template_bulk_maintenance}
                                                onChange={(_, newValue) => setWaForm(f => ({ ...f, template_bulk_maintenance: newValue || '' }))}
                                                onInputChange={(_, newInputValue) => setWaForm(f => ({ ...f, template_bulk_maintenance: newInputValue }))}
                                                renderInput={(params) => (
                                                    <TextField {...params} fullWidth label="Maintenance Template"
                                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Autocomplete
                                                freeSolo
                                                fullWidth
                                                options={approvedTemplates.map(t => t.name)}
                                                value={waForm.template_bulk_feature}
                                                onChange={(_, newValue) => setWaForm(f => ({ ...f, template_bulk_feature: newValue || '' }))}
                                                onInputChange={(_, newInputValue) => setWaForm(f => ({ ...f, template_bulk_feature: newInputValue }))}
                                                renderInput={(params) => (
                                                    <TextField {...params} fullWidth label="Feature Template"
                                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <Autocomplete
                                                freeSolo
                                                fullWidth
                                                options={approvedTemplates.map(t => t.name)}
                                                value={waForm.template_bulk_offer}
                                                onChange={(_, newValue) => setWaForm(f => ({ ...f, template_bulk_offer: newValue || '' }))}
                                                onInputChange={(_, newInputValue) => setWaForm(f => ({ ...f, template_bulk_offer: newInputValue }))}
                                                renderInput={(params) => (
                                                    <TextField {...params} fullWidth label="Offer Template"
                                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                                )}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{ my: 3 }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: theme.palette.primary.main }}>
                                        📩 Webhook & Incoming Reply Forwarding
                                    </Typography>
                                    <Alert severity="info" sx={{ mb: 2, borderRadius: '12px' }}>
                                        When customers reply to your messages, Meta sends their replies to your webhook, which forwards the actual text/audio/video/image to your mobile number below — not just a template alert. This only works while your forwarding number has an open 24-hour WhatsApp session, which the Daily Keep-Alive Template below exists to maintain: it prompts your forwarding number once a day, and that number's own auto-reply (configured on that device, e.g. in WhatsApp Business App — not here) replies back, which is what actually opens the session.
                                    </Alert>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <TextField fullWidth label="Forwarding Mobile Number (Business/Personal)" placeholder="e.g. 201012345678 or 010..." {...waField('forwarding_mobile')}
                                                helperText="Incoming customer replies (text, audio, video, images) are forwarded here directly — requires an open 24h session, see Daily Keep-Alive Template"
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField fullWidth label="Daily Keep-Alive Template" placeholder="daily_checkin" {...waField('template_forward_keepalive')}
                                                helperText="Sent to the forwarding number once a day to prompt its own auto-reply and keep the 24h session open"
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                        </Grid>
                                        <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {waForm.last_forwarding_keepalive_sent_at
                                                    ? `Last keep-alive sent: ${waForm.last_forwarding_keepalive_sent_at}`
                                                    : 'Keep-alive not sent yet — runs automatically once a day.'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Divider sx={{ my: 2 }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: theme.palette.primary.main }}>
                                                🤖 Automated Customer Acknowledgment (Bilingual)
                                            </Typography>
                                            <FormControlLabel
                                                control={<Switch checked={Boolean(waForm.auto_reply_enabled)} onChange={(e) => setWaForm(f => ({ ...f, auto_reply_enabled: e.target.checked }))} color="primary" />}
                                                label="Send instant auto-reply acknowledgment when a customer replies to your bot"
                                                sx={{ mb: 1 }}
                                            />
                                            {waForm.auto_reply_enabled && (
                                                <TextField fullWidth multiline rows={3} label="Auto-Reply Message" {...waField('auto_reply_message')}
                                                    helperText="This message is sent instantly to the customer when they message or reply to your bot (debounced to once every 15 minutes per customer)"
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                            )}
                                        </Grid>
                                    </Grid>
                                </Section>
                            </Collapse>

                            {/* Save WhatsApp */}
                            <Button variant="contained" startIcon={waLoading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                                onClick={handleWASave} disabled={waLoading}
                                sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, px: 4, py: 1.5, bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' } }}>
                                {waLoading ? 'Saving…' : 'Save WhatsApp Settings'}
                            </Button>
                        </>
                    )}
                </Box>
            )}

            {/* ── Tab 2: WhatsApp Templates ── */}
            {activeTab === 'wa-templates' && <WhatsAppTemplatesManager />}

            {/* ── Tab 3: Whish Payments (tenant-facing customer payments) ── */}
            {activeTab === 'whish' && (
                <Box>
                    {twsFetching || modules === null ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
                    ) : !isPro ? (
                        <Section icon={<PaymentsIcon />} title="Whish Payments" subtitle="Let your customers pay you directly via Whish" color={theme.palette.warning.main}>
                            <Alert severity="warning" icon={<LockIcon />} sx={{ borderRadius: '12px' }}>
                                This feature isn't included in your plan. Get it enabled to let your
                                customers pay their invoices directly via Whish, using your own Whish merchant account.
                            </Alert>
                        </Section>
                    ) : (
                        <>
                            <Section icon={<PaymentsIcon />} title="Whish Payments" subtitle="Accept payments from your own customers via Whish" color={theme.palette.primary.main}
                                action={
                                    <Button
                                        size="small"
                                        variant={twsCredsEditing ? 'contained' : 'outlined'}
                                        color={twsCredsEditing ? 'warning' : 'primary'}
                                        startIcon={twsCredsEditing ? <LockIcon /> : <EditIcon />}
                                        onClick={() => setTwsCredsEditing(e => !e)}
                                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                                    >
                                        {twsCredsEditing ? 'Lock' : 'Edit'}
                                    </Button>
                                }>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                                    <Box>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>Enable Whish Payments</Typography>
                                        <Typography variant="body2" color="text.secondary">Let customers pay you directly using the credentials below</Typography>
                                    </Box>
                                    <Switch checked={twsForm.enabled} onChange={e => setTwsForm(f => ({ ...f, enabled: e.target.checked }))} />
                                </Box>
                                <Alert severity="warning" sx={{ mb: 3, borderRadius: '12px' }}>
                                    ⚠️ This section stores your own Whish merchant credentials. Keep these secret — never share them.
                                    {!twsCredsEditing && ' Fields are locked — click Edit above to change them.'}
                                </Alert>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField fullWidth label="Whish Channel" type={showTwsSecret ? 'text' : 'password'} placeholder="Your Whish channel ID"
                                            {...twsField('whish_channel')} disabled={!twsCredsEditing}
                                            InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowTwsSecret(s => !s)}>{showTwsSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}</IconButton></InputAdornment> }}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField fullWidth label="Whish Secret" type={showTwsSecret ? 'text' : 'password'} placeholder="Your Whish secret"
                                            {...twsField('whish_secret')} disabled={!twsCredsEditing}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth label="Display Name (optional)" placeholder="Shown to your customers instead of your internal business name"
                                            {...twsField('display_name_override')} disabled={!twsCredsEditing}
                                            helperText="Not shown on the payment page yet — captured for future use"
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                    </Grid>
                                </Grid>
                            </Section>
                            <Button variant="contained" startIcon={twsLoading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                                onClick={handleTwsSave} disabled={twsLoading}
                                sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, px: 4, py: 1.5 }}>
                                {twsLoading ? 'Saving…' : 'Save Whish Payment Settings'}
                            </Button>

                            <Box sx={{ mt: 3 }} />
                            <Section icon={<LinkIcon />} title="Public Payment Page" subtitle="One link, safe to hand out to anyone -- customers self-identify by phone and pay whatever they owe" color={theme.palette.secondary.main}>
                                {!publicPayLink ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
                                ) : (
                                    <>
                                        <TextField
                                            fullWidth label="Payment page link" value={publicPayLink.url || ''} InputProps={{ readOnly: true }}
                                            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                        />
                                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                            <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={copyPublicPayLink}
                                                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
                                                {copied ? 'Copied!' : 'Copy Link'}
                                            </Button>
                                            <Button variant="outlined" color="warning" startIcon={<AutorenewIcon />}
                                                onClick={() => setRegenerateConfirmOpen(true)} disabled={publicPayLinkLoading}
                                                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
                                                Regenerate Link
                                            </Button>
                                        </Box>
                                        <Alert severity="info" sx={{ mt: 2, borderRadius: '12px' }}>
                                            Regenerating breaks the current link -- anyone who saved or bookmarked it will no longer be able to pay through it.
                                        </Alert>
                                    </>
                                )}
                            </Section>
                        </>
                    )}
                </Box>
            )}

            <Dialog open={regenerateConfirmOpen} onClose={() => setRegenerateConfirmOpen(false)}>
                <DialogTitle>Regenerate the public payment link?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This replaces your current public payment page link with a brand-new one. The old link will stop
                        working immediately for anyone who has it saved, bookmarked, or shared. This can't be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRegenerateConfirmOpen(false)} disabled={publicPayLinkLoading}>Cancel</Button>
                    <Button onClick={generateOrRegeneratePublicPayLink} color="warning" variant="contained" disabled={publicPayLinkLoading}>
                        {publicPayLinkLoading ? 'Regenerating…' : 'Regenerate'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={agentRegenerateConfirmOpen} onClose={() => setAgentRegenerateConfirmOpen(false)}>
                <DialogTitle>Regenerate the agent token?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This invalidates the current token immediately -- the on-prem agent authenticating with it
                        will stop working and go offline right away. It will stay offline until the new token is
                        pasted into <code>agent.toml</code> on the on-prem box and the agent is restarted. This
                        can't be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAgentRegenerateConfirmOpen(false)} disabled={agentActionLoading}>Cancel</Button>
                    <Button onClick={handleRegenerateToken} color="warning" variant="contained" disabled={agentActionLoading}>
                        {agentActionLoading ? 'Regenerating…' : 'Regenerate'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Agent token reveal -- shown exactly once, on create or regenerate.
                revealedToken is cleared the moment this dialog closes (see
                handleCloseTokenDialog); there is no way to see this value again
                anywhere in the app afterwards. */}
            <Dialog open={tokenDialogOpen} onClose={handleCloseTokenDialog} fullWidth maxWidth="sm">
                <DialogTitle>Agent Installation Required</DialogTitle>
                <DialogContent>
                    <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }}>
                        An automated installer script (<strong>Install-ServiceBillsAgent.ps1</strong>) has been downloaded.
                        Run it on the server/PC that can reach your local devices: right-click it → <strong>Run with PowerShell</strong>.
                        If Windows blocks it, run <code>powershell -ExecutionPolicy Bypass -File .\Install-ServiceBillsAgent.ps1</code> from the Downloads folder.
                    </Alert>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        The script automatically sets up directories, downloads agent files, installs Python dependencies, registers a <strong>Windows Scheduled Task</strong> (running as SYSTEM at boot, never ending, and auto-restarting on failure), and starts the agent immediately.
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        After it finishes, please open <strong>C:\ProgramData\ServiceBillsAgent\agent.toml</strong> in Notepad to add your device IP addresses and passwords.
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        If you need to configure it manually, your agent token is:
                    </Typography>
                    <TextField
                        fullWidth
                        value={revealedToken || ''}
                        inputRef={tokenInputRef}
                        InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button startIcon={<DownloadIcon />} onClick={() => downloadInstallerScript(revealedToken)}>
                        Download Script Again
                    </Button>
                    <Button startIcon={<ContentCopyIcon />} onClick={handleCopyToken}>
                        {tokenCopied ? 'Copied!' : 'Copy Token'}
                    </Button>
                    <Button variant="contained" onClick={handleCloseTokenDialog}>Done</Button>
                </DialogActions>
            </Dialog>

            {/* ── Tab 4: Expense Categories ── */}
            {activeTab === 'expense-categories' && (
                <Section icon={<MessageIcon />} title="Expense Categories" subtitle="Manage the categories used to classify expenses" color={theme.palette.warning.main}>
                    <ExpenseCategoryManager />
                </Section>
            )}

            {/* ── Tab 5: User Management ── */}
            {activeTab === 'users' && (
                <UserManagement />
            )}

            {/* Tab 6: Sectors */}
            {activeTab === 'sectors' && (
                <SectorManager />
            )}

            {activeTab === 'ai-connector' && (
                <AIConnectorCard />
            )}

            {/* Tab 7: License (on-prem only) */}
            {activeTab === 'license' && <LicenseTab setSnackbar={setSnackbar} />}

        </Box>
    );
};

export default SettingsView;
